/**
 * usePlacementVergleich — TanStack Query hooks for the Ausschreibungs-Screen Tab 4b (KI-Offertvergleich
 * + Offer Score + Empfehlung, Prozessschritte 11/12; P2 Placement Slice 11).
 *
 * Server state = TanStack Query ONLY. live mode hits apiClient.placements.*Vergleich + apiClient.offerScore.*;
 * mock mode resolves the Ü5/Ü7 fixture (Helvetia rang 1, AXA rang 2, Zurich rang 3 with E4). The server
 * owns every decision; the UI only triggers + renders. While the KI generation is laufend the query
 * refetches every 4 s. Mutations invalidate the vergleich key, the offerten key (badges flip to
 * «Im Vergleich») and, after a run/freigabe, the detail + list keys (the status badge moves).
 *
 * [Source: apps/web/src/features/placements/usePlacementOfferten.ts] (live/mock + qk + invalidate idiom)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import {
  berechneOffertVergleich,
  COMPARISON_GENERATION_STATUS_LABELS,
  COMPARISON_REPORT_STATUS_LABELS,
  OFFER_SCORE_GEWICHTE_DEFAULT,
  type ComparisonOfferEintrag,
  type ComparisonReportEintrag,
  type OfferComparisonPatchBody,
  type OfferComparisonResponse,
  type OfferComparisonRunBody,
  type OfferNormalizedV1,
  type OfferScoreGewichteResponse,
  type OfferScoreGewichtePutBody,
  type VergleichInputOffer,
  type VergleichSollRegel,
} from '@brokly/shared';

// ── Ü5/Ü7 mock fixture (mock mode only) ───────────────────────────────────────────

function deckung(rule_key: string | null, label: string, status: 'eingeschlossen' | 'ausgeschlossen', sum: number | null, sollZiel: number | null): OfferNormalizedV1['deckungen'][number] {
  return {
    rule_key,
    label,
    pflicht_status: rule_key ? 'empfehlung' : null,
    status,
    deckungssumme: sum,
    selbstbehalt: null,
    hinweis: null,
    soll_zielsumme: sollZiel,
    soll_selbstbehalt: rule_key ? 2500 : null,
    match: rule_key ? 'rule_key' : 'keine',
  };
}

function normalized(praemie: number, sb: number, bhSumme: number, rueckrufIncl: boolean, ausschluesse: string[]): OfferNormalizedV1 {
  return {
    schema_version: 1,
    waehrung: 'CHF',
    praemie: { jahr: praemie, netto: null, brutto: null },
    versicherungssumme: 5000000,
    selbstbehalt: sb,
    vertragsdauer_monate: 12,
    beginn: null,
    gueltig_bis: null,
    offert_nummer: null,
    versicherer_name: null,
    deckungen: [
      deckung('betriebshaftpflicht_deckung', 'Betriebshaftpflicht', 'eingeschlossen', bhSumme, 5000000),
      deckung(null, 'Produkthaftpflicht', 'eingeschlossen', null, null),
      deckung(null, 'Rueckrufkosten', rueckrufIncl ? 'eingeschlossen' : 'ausgeschlossen', null, null),
      deckung(null, 'USA/Kanada', 'eingeschlossen', null, null),
    ],
    ausschluesse,
    soll_abdeckung: { total: 1, gematcht: 1, unbekannt: 0 },
    kernwerte_vollstaendig: true,
    quelle: { extractor: 'fixture', confidence: 0.95, extracted_at: '2026-10-05T00:00:00.000Z', manuell_ergaenzt: false },
  };
}

const MOCK_META: Record<string, { carrier: string; submission: string; staerken: string[]; schwaechen: string[]; begruendung: string }> = {
  'offd-helvetia': {
    carrier: 'Helvetia',
    submission: 'SUB-2026-000484-HAFT',
    staerken: ['Hoechster Offer Score im Vergleich', 'Erfuellt alle Soll-Vorgaben aus Schritt 4'],
    schwaechen: ['Praemie ueber der guenstigsten Offerte'],
    begruendung: 'Rang 1 mit dem hoechsten Score.',
  },
  'offd-axa': {
    carrier: 'AXA',
    submission: 'SUB-2026-000482-HAFT',
    staerken: ['Erfuellt alle Soll-Vorgaben aus Schritt 4'],
    schwaechen: ['Praemie ueber der guenstigsten Offerte', 'Ausschluesse: Asbest, Cyber-Schaeden'],
    begruendung: 'Rang 2, solide Deckung.',
  },
  'offd-zurich': {
    carrier: 'Zurich',
    submission: 'SUB-2026-000483-HAFT',
    staerken: ['Tiefste Jahrespraemie der verglichenen Offerten'],
    schwaechen: ['Mindestanforderung(en) nicht erfuellt: Betriebshaftpflicht - Score um 10 Punkte abgewertet', 'Rueckrufkosten nicht eingeschlossen (bei 2 von 3 Versicherern enthalten)'],
    begruendung: 'Rang 3, Mindestanforderung verfehlt.',
  },
};

function mockReport(): ComparisonReportEintrag {
  const soll: VergleichSollRegel[] = [
    { rule_key: 'betriebshaftpflicht_deckung', label: 'Betriebshaftpflicht', pflicht_status: 'empfehlung', effective_target_value: 5000000, selbstbehalt_override: 2500, status: 'bestaetigt' },
  ];
  const offerten: VergleichInputOffer[] = [
    { offer_document_id: 'offd-axa', submission_id: 'sub-axa', submission_number: 'SUB-2026-000482-HAFT', carrier_uid: 'CHE-axa', carrier_name: 'AXA', logo_url: null, version_no: 1, normalized: normalized(18400, 2500, 5000000, true, ['Asbest', 'Cyber-Schaeden']), eignung: 'geeignet', aktive_policen: 14, broker: { service_subscore: null, individuell_subscore: null } },
    { offer_document_id: 'offd-zurich', submission_id: 'sub-zurich', submission_number: 'SUB-2026-000483-HAFT', carrier_uid: 'CHE-zurich', carrier_name: 'Zurich', logo_url: null, version_no: 1, normalized: normalized(16900, 5000, 2000000, false, ['Asbest']), eignung: 'geeignet', aktive_policen: 9, broker: { service_subscore: null, individuell_subscore: null } },
    { offer_document_id: 'offd-helvetia', submission_id: 'sub-helvetia', submission_number: 'SUB-2026-000484-HAFT', carrier_uid: 'CHE-helvetia', carrier_name: 'Helvetia', logo_url: null, version_no: 1, normalized: normalized(19200, 2500, 5000000, true, ['Asbest']), eignung: 'geeignet', aktive_policen: 21, broker: { service_subscore: null, individuell_subscore: null } },
  ];
  const r = berechneOffertVergleich({ offerten, soll, gewichte: OFFER_SCORE_GEWICHTE_DEFAULT });

  const eintraege: ComparisonOfferEintrag[] = r.offerten.map((e) => {
    const meta = MOCK_META[e.offer_document_id]!;
    return {
      ...e,
      submission_id: `sub-${e.offer_document_id}`,
      submission_number: meta.submission,
      carrier_uid: `CHE-${e.offer_document_id}`,
      carrier_name: meta.carrier,
      logo_url: null,
      version_no: 1,
      ki_staerken: meta.staerken,
      ki_schwaechen: meta.schwaechen,
      ki_begruendung: meta.begruendung,
      staerken: null,
      schwaechen: null,
      broker_hinweis: null,
      effektive_staerken: meta.staerken,
      effektive_schwaechen: meta.schwaechen,
      service_subscore: null,
      individuell_subscore: null,
      ist_empfehlung: e.offer_document_id === r.score_empfehlung_offer_id,
      ist_score_empfehlung: e.offer_document_id === r.score_empfehlung_offer_id,
    };
  });

  return {
    id: 'rep-mock-1',
    lauf_nr: 1,
    status: 'entwurf',
    status_label: COMPARISON_REPORT_STATUS_LABELS.entwurf,
    is_aktuell: true,
    generation_status: 'fertig',
    generation_status_label: COMPARISON_GENERATION_STATUS_LABELS.fertig,
    generation_seq: 1,
    generator: { driver: 'fixture', model: 'fixture-v1', confidence: 0.9, is_demo: true },
    generated_at: '2026-10-05T09:00:00.000Z',
    gewichte: OFFER_SCORE_GEWICHTE_DEFAULT,
    gewichte_quelle: 'tenant',
    zeilen: r.zeilen,
    offerten: eintraege,
    score_empfehlung_offer_id: r.score_empfehlung_offer_id,
    empfehlung_offer_id: r.score_empfehlung_offer_id,
    empfehlung_begruendung: null,
    empfehlung_abweichend: false,
    ki_zusammenfassung: '3 Offerten verglichen. Empfehlung nach Score: Helvetia. Der Score unterstuetzt die fachliche Beurteilung, ersetzt sie aber nicht.',
    zusammenfassung: null,
    effektive_zusammenfassung: '3 Offerten verglichen. Empfehlung nach Score: Helvetia. Der Score unterstuetzt die fachliche Beurteilung, ersetzt sie aber nicht.',
    broker_edited: false,
    broker_edited_at: null,
    broker_edited_by_name: null,
    freigegeben_at: null,
    freigegeben_by_name: null,
    docx: { vorhanden: false, filename: null, size_bytes: null, generated_at: null, veraltet: true, datei_url: null, rendered_with: null },
    veraltet: false,
    veraltet_grund: null,
    input_offer_ids: ['offd-axa', 'offd-zurich', 'offd-helvetia'],
    created_at: '2026-10-05T09:00:00.000Z',
    updated_at: '2026-10-05T09:00:00.000Z',
  };
}

function mockVergleich(id: string): OfferComparisonResponse {
  return {
    placement_id: id,
    placement_number: 'PL-2026-000001',
    status: 'verglichen',
    branchencode: '21000',
    bericht: mockReport(),
    ausfuehren_erlaubt: true,
    sperrgrund: null,
    anzahl_normalisiert: 3,
    bearbeiten_erlaubt: true,
    freigeben_erlaubt: false,
    gewichte_tenant: OFFER_SCORE_GEWICHTE_DEFAULT,
  };
}

function mockGewichte(): OfferScoreGewichteResponse {
  return { gewichte: OFFER_SCORE_GEWICHTE_DEFAULT, origin: 'system', customized_at: null, catalog_version: 'u7-v1', default: OFFER_SCORE_GEWICHTE_DEFAULT };
}

// ── Query ──────────────────────────────────────────────────────────────────────

export function usePlacementVergleich(id: string) {
  const live = isLiveData();
  return useQuery<OfferComparisonResponse>({
    queryKey: qk.placementVergleich(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.getVergleich(id) : Promise.resolve(mockVergleich(id))),
    refetchInterval: (query) =>
      live && query.state.data?.bericht?.generation_status === 'laufend' ? 4000 : false,
    ...(live ? {} : { initialData: () => mockVergleich(id), staleTime: Infinity }),
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

function useVergleichInvalidate(id: string) {
  const qc = useQueryClient();
  return (opts: { status?: boolean } = {}) => {
    void qc.invalidateQueries({ queryKey: ['placements', 'vergleich', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'offerten', id] });
    if (opts.status) {
      void qc.invalidateQueries({ queryKey: ['placements', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['placements', 'list'] });
    }
  };
}

export function useRunVergleich(id: string) {
  const live = isLiveData();
  const invalidate = useVergleichInvalidate(id);
  return useMutation({
    mutationFn: (body: OfferComparisonRunBody = {}) => (live ? apiClient.placements.runVergleich(id, body) : Promise.resolve(mockVergleich(id))),
    onSuccess: () => invalidate({ status: true }),
  });
}

export function usePatchVergleich(id: string) {
  const live = isLiveData();
  const invalidate = useVergleichInvalidate(id);
  return useMutation({
    mutationFn: ({ rid, body }: { rid: string; body: OfferComparisonPatchBody }) =>
      live ? apiClient.placements.patchVergleich(id, rid, body) : Promise.resolve(mockVergleich(id)),
    onSuccess: () => invalidate(),
  });
}

export function useKiNeuVergleich(id: string) {
  const live = isLiveData();
  const invalidate = useVergleichInvalidate(id);
  return useMutation({
    mutationFn: ({ rid }: { rid: string }) => (live ? apiClient.placements.kiNeuVergleich(id, rid) : Promise.resolve(mockVergleich(id))),
    onSuccess: () => invalidate(),
  });
}

export function useFreigebenVergleich(id: string) {
  const live = isLiveData();
  const invalidate = useVergleichInvalidate(id);
  return useMutation({
    mutationFn: ({ rid }: { rid: string }) => (live ? apiClient.placements.freigebenVergleich(id, rid) : Promise.resolve(mockVergleich(id))),
    onSuccess: () => invalidate({ status: true }),
  });
}

export function useExportVergleich(id: string) {
  const live = isLiveData();
  const invalidate = useVergleichInvalidate(id);
  return useMutation({
    mutationFn: ({ rid }: { rid: string }) => (live ? apiClient.placements.exportVergleich(id, rid) : Promise.resolve(mockVergleich(id))),
    onSuccess: () => invalidate(),
  });
}

// ── Offer-Score weights (Regelwerk admin) ────────────────────────────────────────

export function useOfferScoreGewichte() {
  const live = isLiveData();
  return useQuery<OfferScoreGewichteResponse>({
    queryKey: qk.offerScoreGewichte(live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.offerScore.getGewichte() : Promise.resolve(mockGewichte())),
    ...(live ? {} : { initialData: () => mockGewichte(), staleTime: Infinity }),
  });
}

export function usePutOfferScoreGewichte() {
  const live = isLiveData();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OfferScoreGewichtePutBody) => (live ? apiClient.offerScore.putGewichte(body) : Promise.resolve(mockGewichte())),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['offer-score', 'gewichte'] }),
  });
}

export function useResetOfferScoreGewichte() {
  const live = isLiveData();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => (live ? apiClient.offerScore.resetGewichte() : Promise.resolve(mockGewichte())),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['offer-score', 'gewichte'] }),
  });
}
